import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { StringHelper } from "@/Common/Helpers/StringHelper";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicalItemAlertsAndSpecialNeeds } from "@/Patient/Models/MedicalSummary/MedicalItemAlertsAndAllergies";
import { MedicalItemFamilyHistory } from "@/Patient/Models/MedicalSummary/MedicalItemFamilyHistory";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemMedicalReaction } from "@/Patient/Models/MedicalSummary/MedicalItemMedicalReaction";
import { MedicalItemMedicationAndPrescription } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicalItemProblemAndCondition } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { MedicalItemScreeningTestResponse } from "@/Patient/Models/MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";

export class MedicalNoteIndexHelper {
    /**
     * Return all medical item's lower-cased search index of encounters by medical summary
     * @param {IPatientMedicalSummaryModel} medicalSummary
     * @returns {Record<string, string>}
     */
    public static buildEncounterIndexByMedicalSummaryModel(medicalSummary: IPatientMedicalSummaryModel): Record<string, string> {
        let index: Record<string, string> = {};
        const medicalProblemsSearchIndex: Record<string, string> = this.getMedicalProblemsSearchIndex(medicalSummary);
        const preventiveActivitiesSearchIndex: Record<string, string> = this.getPreventiveActivitiesSearchIndex(medicalSummary);
        const allergiesAndIntolerancesSearchIndex: Record<string, string> = this.getAllergiesAndIntolerancesSearchIndex(medicalSummary);
        const prescriptionsSearchIndex: Record<string, string> = this.getPrescriptionsSearchIndex(medicalSummary);
        const riskFactorsSearchIndex: Record<string, string> = this.getRiskFactorsSearchIndex(medicalSummary);
        const alertAndSpecialNeedsSearchIndex: Record<string, string> = this.getAlertAndSpecialNeeds(medicalSummary);
        const immunizationsSearchIndex: Record<string, string> = this.getImmunizationsSearchIndex(medicalSummary);
        const familyHistorySearchIndex: Record<string, string> = this.getFamilyHistorySearchIndex(medicalSummary);
        const followUpsSearchIndex: Record<string, string> = this.getFollowUp(medicalSummary);
        const formsSearchIndex: Record<string, string> = this.getFormsSearchIndex(medicalSummary);

        index = this.makeMergedIndex(index, medicalProblemsSearchIndex);
        index = this.makeMergedIndex(index, preventiveActivitiesSearchIndex);
        index = this.makeMergedIndex(index, allergiesAndIntolerancesSearchIndex);
        index = this.makeMergedIndex(index, prescriptionsSearchIndex);
        index = this.makeMergedIndex(index, riskFactorsSearchIndex);
        index = this.makeMergedIndex(index, alertAndSpecialNeedsSearchIndex);
        index = this.makeMergedIndex(index, immunizationsSearchIndex);
        index = this.makeMergedIndex(index, familyHistorySearchIndex);
        index = this.makeMergedIndex(index, followUpsSearchIndex);
        index = this.makeMergedIndex(index, formsSearchIndex);

        return index;
    }

    /**
     * Return all lower-cased search index string by an encounter
     * @param {Encounter} encounter
     * @returns {string}
     */
    public static buildEncounterIndexByEncounter(encounter: Encounter): string {
        if (!encounter)
            return "";

        const consultationType = this.getConsultationText(encounter);
        const relatedEmployeesSearchIndex = this.getRelatedEmployeesName(encounter);
        const rawTextSearchIndex = this.getRawText(encounter);

        const mergedArray = [
            ...consultationType,
            ...relatedEmployeesSearchIndex,
            ...rawTextSearchIndex
        ];
        const uniqueArray = StringHelper.makeUniqueArray(mergedArray);

        return StringHelper.makeLowercasedMergedString(uniqueArray);
    }

    /**
     * Return an array of non-duplicate names
     * @param employee an employee who is related with the current encounter
     * @returns {string[]}
     */
    private static getEmployeesValidNameList(employee: Employee): string[] {
        const names = [];

        if (!IsNullOrWhiteSpace(employee?.FirstName)) {
            names.push(employee.FirstName);
        }

        if (!IsNullOrWhiteSpace(employee?.LastName)) {
            names.push(employee.LastName);
        }

        return names;
    }

    /**
     * Return an encounter's search index related to employees that have handled the encounter
     * @param {Encounter} encounter
     * @returns  {string[]}
     */
    private static getRelatedEmployeesName(encounter: Encounter): string[] {
        const index: string[] = [];
        const relatedEmployeesNames: string[] = [];
        const signedOffByEmployee = cacheStore.GetActiveEmployees()?.find(p => p.ContactFacet_id === encounter.SignedOffByEmployeeId) ?? null;
        const relatedEmployeesIds = encounter?.EncounterRelatedEmployees?.map(p => p.EmployeeId) ?? [];

        relatedEmployeesNames.push(...this.getEmployeesValidNameList(signedOffByEmployee));

        for (const relatedEmployeesId of relatedEmployeesIds) {
            const relatedEmployee = cacheStore.GetEmployeeByContactFacetId(relatedEmployeesId);
            relatedEmployeesNames.push(...this.getEmployeesValidNameList(relatedEmployee));
        }

        index.push(...relatedEmployeesNames);

        return index;
    }

    /**
     * Return an encounter's search index related to raw text
     * @param {Encounter} encounter
     * @returns  {string[]}
     */
    private static getRawText(encounter: Encounter): string[] {
        const index: string[] = [];

        index.push(encounter?.VisitPurpose);
        index.push(encounter?.HistoricalNote);
        index.push(encounter?.LabNote);
        index.push(encounter?.ExamenNote);
        index.push(encounter?.RadiologyNote);
        index.push(encounter?.ProgressNote);
        index.push(encounter?.OtherPlanNote);

        return index;
    }

    /**
     * Return an encounter's search index related to consultation of an encounter
     * @param {Encounter} encounter
     * @returns  {string[]}
     */
    private static getConsultationText(encounter: Encounter): string[] {
        const index: string[] = [];

        const consultationTypeValue = cacheStore.GetLocalizedValueByLookupId(encounter?.ConsultationTypeLookupId) ?? "";
        const consultationContextValue = cacheStore.GetLocalizedValueByLookupId(encounter?.ConsultationContextLookupId) ?? "";

        index.push(consultationTypeValue);
        index.push(consultationContextValue);

        return index;
    }

    /**
     * Return an encounter's search index related to medical items in active problems
     * @param {IPatientMedicalSummaryModel} medicalSummary
     * @returns {Record<string, string>}
     */
    private static getMedicalProblemsSearchIndex(medicalSummary: IPatientMedicalSummaryModel): Record<string, string> {
        const index: Record<string, string> = {};
        const allMedicalProblems: MedicalItemProblemAndCondition[] = PatientMedicalSummaryHelper.GetAllMedicalProblemsFromSummary(medicalSummary) ?? [];

        allMedicalProblems?.forEach(medicalProblem => {
            if (!IsNullOrUndefined(medicalProblem?.RelatedEncounterId)) {
                const relatedEncounterId = medicalProblem.RelatedEncounterId;
                const currentRelatedEncounterString = index[relatedEncounterId];
                const stringArray: string[] = [currentRelatedEncounterString];

                stringArray.push(medicalProblem.Procedure);
                stringArray.push(medicalProblem.ProblemDescription);
                stringArray.push(medicalProblem.ProblemStatusLookupValue);

                medicalProblem.QualifiedDetails?.forEach(qualifiedDetail => {
                    if (qualifiedDetail) {
                        stringArray.push(qualifiedDetail.Code);
                        stringArray.push(qualifiedDetail.Text);
                    }
                });

                const uniqueArray = StringHelper.makeUniqueArray(stringArray);

                index[relatedEncounterId] = StringHelper.makeMergedString(uniqueArray);
            }
        });

        return index;
    }

    /**
     * Return an encounter's search index related to medical items in preventive activity
     * @param {IPatientMedicalSummaryModel} medicalSummary
     * @returns {Record<string, string>}
     */
    private static getPreventiveActivitiesSearchIndex(medicalSummary: IPatientMedicalSummaryModel): Record<string, string> {
        const index: Record<string, string> = {};
        const preventiveActivities: MedicalItemScreeningTestResponse[] = (medicalSummary.get("MedicalItemsScreeningTest")?.toArray() ?? []);

        preventiveActivities?.forEach(preventiveActivity => {
            if (!IsNullOrUndefined(preventiveActivity?.RelatedEncounterId)) {
                const relatedEncounterId = preventiveActivity.RelatedEncounterId;
                const currentRelatedEncounterString = index[relatedEncounterId];
                const testDefinition = cacheStore.getTestDefinitionById(preventiveActivity.ScreeningTestDefinitionId)?.Name ?? "";
                const stringArray: string[] = [currentRelatedEncounterString, testDefinition];

                preventiveActivity.FollowUps.forEach(followUp => {
                    if (followUp) {
                        stringArray.push(followUp.Reason);
                        stringArray.push(followUp.Description);
                    }
                });

                const uniqueArray = StringHelper.makeUniqueArray(stringArray);

                index[relatedEncounterId] = StringHelper.makeMergedString(uniqueArray);
            }
        });

        return index;
    }

    /**
     * Return an encounter's search index related to medical items in allergies and intolerances
     * @param {IPatientMedicalSummaryModel} medicalSummary
     * @returns {Record<string, string>}
     */
    private static getAllergiesAndIntolerancesSearchIndex(medicalSummary: IPatientMedicalSummaryModel): Record<string, string> {
        const index: Record<string, string> = {};
        const allergiesAndIntolerances: MedicalItemMedicalReaction[] = medicalSummary.get("MedicalItemsMedicalReaction")?.toArray() ?? [];

        if (!isArrayNullOrUndefinedOrEmpty(allergiesAndIntolerances)) {
            for (const medicalReaction of allergiesAndIntolerances) {
                if (!IsNullOrUndefined(medicalReaction?.RelatedEncounterId)) {
                    const relatedEncounterId = medicalReaction.RelatedEncounterId;
                    const currentRelatedEncounterString = index[relatedEncounterId];
                    const {
                        OffendingAgent: offendingAgent,
                        CodeGEN: codeGEN,
                        ReactionDescription: reactionDescription,
                        ClassPharmacological: classPharmacological,
                        SeverityLookupValue: severityLookupValue,
                        ReactionTypeLookupValue: reactionTypeLookupValue,
                        ClinicalStatusLookupValue: clinicalStatusLookupValue,
                        TypeLookupValue: typeLookupValue
                    } = medicalReaction;

                    const mergedArray = [
                        currentRelatedEncounterString,
                        offendingAgent,
                        codeGEN,
                        reactionDescription,
                        classPharmacological,
                        severityLookupValue,
                        reactionTypeLookupValue,
                        clinicalStatusLookupValue,
                        typeLookupValue
                    ];
                    const uniqueArray = StringHelper.makeUniqueArray(mergedArray);

                    index[relatedEncounterId] = StringHelper.makeMergedString(uniqueArray);
                }
            }
        }

        return index;
    }

    /**
     * Return an encounter's search index related to medical items in prescriptions
     * @param {IPatientMedicalSummaryModel} medicalSummary
     * @returns {Record<string, string>}
     */
    private static getPrescriptionsSearchIndex(medicalSummary: IPatientMedicalSummaryModel): Record<string, string> {
        const index: Record<string, string> = {};
        const prescriptions: MedicalItemMedicationAndPrescription[] = medicalSummary.get("MedicalItemsMedicationAndPrescription")?.toArray()?.map(x => x.Model) ?? [];

        prescriptions.forEach(prescription => {
            if (!IsNullOrUndefined(prescription?.RelatedEncounterId)) {
                const relatedEncounterId = prescription.RelatedEncounterId;
                const currentRelatedEncounterString = index[relatedEncounterId];
                const {
                    Name: name,
                    Form: form,
                    DIN,
                    Description: description,
                    StrengthUnit: strengthUnit,
                    Route: route,
                    Instruction: instruction,
                    LegalStatus: legalStatus,
                    TherapeuticIntention: therapeuticIntention,
                    ProtocolId: protocolId,
                    CeaseReason: ceaseReason,
                    TreatmentTypeLookupValue: treatmentTypeLookupValue,
                    InitialRefillQuantityTypeLookupValue: initialRefillQuantityTypeLookupValue,
                    RefillQuantityTypeLookupValue: refillQuantityTypeLookupValue,
                    PrescriptionStatusLookupValue: prescriptionStatusLookupValue,
                    DsqId,
                    DsqOIDVersion,
                    SubstituableReason: substituableReason,
                    Ingredients: ingredients
                } = prescription;

                const mergedArray = [
                    currentRelatedEncounterString,
                    name,
                    form,
                    DIN,
                    description,
                    strengthUnit,
                    route,
                    instruction,
                    legalStatus,
                    therapeuticIntention,
                    protocolId,
                    ceaseReason,
                    treatmentTypeLookupValue,
                    initialRefillQuantityTypeLookupValue,
                    refillQuantityTypeLookupValue,
                    prescriptionStatusLookupValue,
                    DsqId,
                    DsqOIDVersion,
                    substituableReason,
                    ingredients
                ];
                const uniqueArray = StringHelper.makeUniqueArray(mergedArray);

                index[relatedEncounterId] = StringHelper.makeMergedString(uniqueArray);
            }
        });

        return index;
    }

    /**
     * Return an encounter's search index related to medical items in risk factors
     * @param {IPatientMedicalSummaryModel} medicalSummary
     * @returns {Record<string, string>}
     */
    private static getRiskFactorsSearchIndex(medicalSummary: IPatientMedicalSummaryModel): Record<string, string> {
        const index: Record<string, string> = {};
        const medicalItemsRiskFactors: MedicalItemRiskFactor[] = medicalSummary.get("MedicalItemsRiskFactor")?.toArray() ?? [];

        if (!isArrayNullOrUndefinedOrEmpty(medicalItemsRiskFactors)) {
            for (const riskFactor of medicalItemsRiskFactors) {
                if (!IsNullOrUndefined(riskFactor?.RelatedEncounterId)) {
                    const relatedEncounterId = riskFactor.RelatedEncounterId;
                    const currentRelatedEncounterString = index[relatedEncounterId];
                    const {
                        Name: name,
                        ExposureDetails: exposureDetails
                    } = riskFactor;

                    const mergedArray = [currentRelatedEncounterString, name, exposureDetails];
                    const uniqueArray = StringHelper.makeUniqueArray(mergedArray);

                    index[relatedEncounterId] = StringHelper.makeMergedString(uniqueArray);
                }
            }
        }

        return index;
    }

    /**
     * Return an encounter's search index related to medical items in alert and special needs
     * @param {IPatientMedicalSummaryModel} medicalSummary
     * @returns {Record<string, string>}
     */
    private static getAlertAndSpecialNeeds(medicalSummary: IPatientMedicalSummaryModel): Record<string, string> {
        const index: Record<string, string> = {};
        const alertAndSpecialNeeds: MedicalItemAlertsAndSpecialNeeds[] = medicalSummary.get("MedicalItemsAlertAndSpecialNeed")?.toArray() ?? [];

        if (!isArrayNullOrUndefinedOrEmpty(alertAndSpecialNeeds)) {
            for (const alertAndSpecialNeed of alertAndSpecialNeeds) {
                if (!IsNullOrUndefined(alertAndSpecialNeed?.RelatedEncounterId)) {
                    const relatedEncounterId = alertAndSpecialNeed.RelatedEncounterId;
                    const currentRelatedEncounterString = index[relatedEncounterId];
                    const {
                        Name: name,
                        Description: description
                    } = alertAndSpecialNeed;

                    const mergedArray = [currentRelatedEncounterString, name, description];
                    const uniqueArray = StringHelper.makeUniqueArray(mergedArray);

                    index[relatedEncounterId] = StringHelper.makeMergedString(uniqueArray);
                }
            }
        }

        return index;
    }

    /**
     * Return an encounter's search index related to medical items in immunizations
     * @param {IPatientMedicalSummaryModel} medicalSummary
     * @returns {Record<string, string>}
     */
    private static getImmunizationsSearchIndex(medicalSummary: IPatientMedicalSummaryModel): Record<string, string> {
        const index: Record<string, string> = {};
        const immunizations: MedicalItemImmunization[] = medicalSummary.get("MedicalItemsImmunization")?.toArray() ?? [];

        if (!isArrayNullOrUndefinedOrEmpty(immunizations)) {
            for (const immunization of immunizations) {
                if (!IsNullOrUndefined(immunization?.RelatedEncounterId)) {
                    const relatedEncounterId = immunization.RelatedEncounterId;
                    const currentRelatedEncounterString = index[relatedEncounterId];
                    const {
                        Name: name,
                        LotNumber: lotNumber,
                        Type: type,
                        Manufacturer: manufacurer,
                        RouteCode: routeCode,
                        Instruction: instruction,
                        DoseUnitLookupValue: doseUnitLookupValue
                    } = immunization;

                    const mergedArray = [currentRelatedEncounterString, name, lotNumber, type, manufacurer, routeCode, instruction, doseUnitLookupValue];
                    const uniqueArray = StringHelper.makeUniqueArray(mergedArray);

                    index[relatedEncounterId] = StringHelper.makeMergedString(uniqueArray);
                }
            }
        }

        return index;
    }

    /**
     * Return an encounter's search index related to medical items in family history
     * @param {IPatientMedicalSummaryModel} medicalSummary
     * @returns {Record<string, string>}
     */
    private static getFamilyHistorySearchIndex(medicalSummary: IPatientMedicalSummaryModel): Record<string, string> {
        const index: Record<string, string> = {};
        const familyHistorys: MedicalItemFamilyHistory[] = medicalSummary.get("MedicalItemsFamilyHistory")?.toArray() ?? [];

        if (!isArrayNullOrUndefinedOrEmpty(familyHistorys)) {
            for (const familyHistory of familyHistorys) {
                if (!IsNullOrUndefined(familyHistory?.RelatedEncounterId)) {
                    const relatedEncounterId = familyHistory.RelatedEncounterId;
                    const currentRelatedEncounterString = index[relatedEncounterId];
                    const {
                        Treatment: treatment,
                        Description: description,
                        SpecifiedRelationship: specifiedRelationship,
                        Problem: problem,
                        RelationshipLookupValue: relationshipLookupValue
                    } = familyHistory;

                    const mergedArray = [currentRelatedEncounterString, treatment, description, specifiedRelationship, problem, relationshipLookupValue];
                    const uniqueArray = StringHelper.makeUniqueArray(mergedArray);

                    index[relatedEncounterId] = StringHelper.makeMergedString(uniqueArray);
                }
            }
        }

        return index;
    }

    /**
     * Return an encounter's search index related to follow-ups
     * @param {IPatientMedicalSummaryModel} medicalSummary
     * @param {Encounter} encounter
     * @returns  {Record<string,string>}
     */
    private static getFollowUp(medicalSummary: IPatientMedicalSummaryModel): Record<string, string> {
        const index: Record<string, string> = {};
        const followUps = medicalSummary.get("FollowUps")?.toArray() ?? [];

        for (const followUp of followUps) {
            if (!IsNullOrUndefined(followUp?.EncounterId)) {
                const encounterId = followUp.EncounterId;
                const currentEncounterString = index[encounterId];
                const stringArray = [currentEncounterString];

                switch (followUp.MedicalRequestType) {
                    case MedicalRequestType.ConsultationRequest:
                        stringArray.push(followUp.Speciality);
                        break;
                    case MedicalRequestType.LabRequest:
                        stringArray.push(followUp.Reason);
                        stringArray.push(followUp.Description);
                        break;
                }

                const uniqueArray = StringHelper.makeUniqueArray(stringArray);
                index[encounterId] = StringHelper.makeMergedString(uniqueArray);
            }
        }

        return index;
    }

    /**
     * Get all forms titles by encounter from a medical summary
     * @param medicalSummary The medical summary model
     * @returns The forms titles index by encounter
     */
    private static getFormsSearchIndex(medicalSummary: IPatientMedicalSummaryModel): Record<string, string> {
        const index: Record<string, string> = {};
        const encounterFormsTitles = medicalSummary.get("EncounterFormsTitles") ?? {};

        for (const encounterId in encounterFormsTitles)
            index[encounterId] = StringHelper.makeMergedString(Array.from(encounterFormsTitles[encounterId]));

        return index;
    }

    /**
     * Return a merged index by another index
     * @param {Record<string, string>} currentIndex
     * @param {Record<string, string>} indexToMerge
     * @returns {Record<string,string>}
     */
    private static makeMergedIndex(currentIndex: Record<string, string>, indexToMerge: Record<string, string>): Record<string, string> {
        const updatedIndex: Record<string, string> = { ...currentIndex };
        const allRelatedEncountersId = Object.keys(indexToMerge);

        for (const relatedEncounterId of allRelatedEncountersId) {
            const currentEncounterString = updatedIndex[relatedEncounterId] ?? "";
            const foundEncounterString = indexToMerge[relatedEncounterId];
            const uniqueArray = StringHelper.makeUniqueArray([currentEncounterString, foundEncounterString]);

            updatedIndex[relatedEncounterId] = StringHelper.makeLowercasedMergedString(uniqueArray);
        }

        return updatedIndex;
    }
}
